import { useState, useEffect, useMemo } from 'react'
import { useNavigate } from 'react-router'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { observer } from 'mobx-react-lite'
import {
  Avatar, Box, Button, Chip, CircularProgress, IconButton, MenuItem,
  Paper, Stack, Tab, Tabs, Tooltip, Typography,
} from '@mui/material'
import { toast } from 'react-toastify'
import {
  People as ContactsIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Close as CloseIcon,
  OpenInNew as ViewIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  Business as BusinessIcon,
  Sell as RoleIcon,
  History as ActivityIcon,
  CalendarToday as DateIcon,
} from '@mui/icons-material'
import { EntityModal } from '../components/shared/EntityModal'
import { PageHeader } from '../components/shared/PageHeader'
import { DataTable, type Column } from '../components/ui/DataTable'
import {
  contactsApi, Contact, UpdateContactPayload,
} from '../lib/api/contactsApi'
import { UniversalActivityTimeline } from '../components/shared/UniversalActivityTimeline'
import { ContactActivity } from '../lib/api/contactsApi'
import { CallHistorySectionGeneric } from '../components/calls/CallHistorySectionGeneric'
import { callLogsApi, CallLog, CallStatus, SaveOutcomePayload } from '../lib/api/callLogsApi'
import { callStore } from '../lib/stores/callStore'
import { themeColors } from '../app/layout/theme'
import { maskPhoneNumber } from '../utils/phoneUtils'
import { authStore } from '../lib/stores/authStore'
import { orgsApi, lobsApi } from '../lib/api/tenantsApi'
import { useOrgFilter, useOrgSelector } from '../context/OrgSelectorContext'
import { ConfirmDialog } from '../components/shared/ConfirmDialog'
import { DeleteActionIcon, EditActionIcon } from '../components/ui/ActionIcons'
import { DeleteDialog } from '../components/shared/DeleteDialog'
import { PageActionButtons } from '../components/shared/PageActionButtons'
import { ContactDrawer } from '../components/drawers/ContactDrawer'
import { ListToolbar, type BulkAction } from '../components/shared/ListToolbar'
import { UnifiedFilters, EMPTY_UNIFIED_FILTERS, FilterDef, countActiveFilters } from '../components/shared/UnifiedFilterDialog'
import { EntityFormSection, EntityDrawerInput } from '../components/shared/EntityFormDrawer'
import { usePermission } from '../lib/hooks/usePermission'

// ── Helpers ───────────────────────────────────────────────────────────────────

function getInitials(name: string) {
  return name.split(' ').slice(0, 2).map(n => n[0]?.toUpperCase() ?? '').join('')
}

const AVATAR_COLORS = [
  '#0070d2', '#2e7d32', '#7b1fa2', '#f57c00', '#c62828', '#00838f', '#1565c0',
]
function avatarColor(name: string) {
  let h = 0; for (const c of name) h = c.charCodeAt(0) + ((h << 5) - h)
  return AVATAR_COLORS[Math.abs(h) % AVATAR_COLORS.length]
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })
}

// ── Call Flow Hook removed (using global callStore)

// ── Edit Contact Dialog ───────────────────────────────────────────────────────
function EditContactDialog({ open, contact, onClose, onSave, isSaving }: {
  open: boolean; contact: Contact; onClose: () => void
  onSave: (p: UpdateContactPayload) => Promise<void>; isSaving: boolean
}) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', role: '' })
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSaveConfirmOpen, setIsSaveConfirmOpen] = useState(false)

  useEffect(() => {
    if (open) {
      setForm({ name: contact.name, phone: contact.phone, email: contact.email ?? '', role: contact.role ?? '' })
      setErrors({})
      setIsSaveConfirmOpen(false)
    }
  }, [open, contact])

  const set = (k: string, v: string) => { setForm(f => ({ ...f, [k]: v })); setErrors(e => ({ ...e, [k]: '' })) }

  const handleSave = () => {
    const e: Record<string, string> = {}
    if (!form.name.trim()) e.name = 'Required'
    if (!form.phone.trim()) e.phone = 'Required'
    if (Object.keys(e).length) { setErrors(e); return }
    setIsSaveConfirmOpen(true)
  }

  const handleConfirmSave = async () => {
    setIsSaveConfirmOpen(false)
    await onSave({ name: form.name.trim(), phone: form.phone.trim(), email: form.email.trim() || null, role: form.role.trim() || null })
  }

  return (
    <>
      <EntityModal
        open={open}
        onClose={onClose}
        onSubmit={handleSave}
        title="Edit Contact"
        submitLabel="Save Changes"
        isSubmitting={isSaving}
        maxWidth="xs"
        icon={<EditIcon sx={{ fontSize: 18 }} />}
      >
        <EntityFormSection columns={1}>
          <EntityDrawerInput
            label="Full Name"
            required
            value={form.name}
            onChange={v => set('name', v)}
            error={errors.name}
          />
          <EntityDrawerInput
            label="Phone"
            required
            value={form.phone}
            onChange={v => set('phone', v)}
            fieldType="phone"
            error={errors.phone}
          />
          <EntityDrawerInput
            label="Email"
            value={form.email}
            onChange={v => set('email', v)}
            fieldType="email"
            error={errors.email}
          />
          <EntityDrawerInput
            label="Role / Title"
            value={form.role}
            onChange={v => set('role', v)}
            placeholder="e.g. Manager, CEO"
          />
        </EntityFormSection>
      </EntityModal>

      <ConfirmDialog
        open={isSaveConfirmOpen}
        title="Save Changes"
        description="Are you sure you want to save the changes to this contact?"
        confirmLabel="Save"
        confirmColor="primary"
        onClose={() => setIsSaveConfirmOpen(false)}
        onConfirm={handleConfirmSave}
      />
    </>
  )
}

// ── Contact Detail Panel ──────────────────────────────────────────────────────
const ContactDetailPanel = observer(({ contact, onClose, canEdit, canDelete, canViewCustomer, onEdit, onDelete }: {
  contact: Contact | null; onClose: () => void
  canEdit: boolean; canDelete: boolean; canViewCustomer: boolean
  onEdit: (c: Contact) => void; onDelete: (c: Contact) => void
}) => {
  const navigate = useNavigate()
  const qc = useQueryClient()
  const [tab, setTab] = useState(0)

  // Reset to Details tab whenever a different contact is selected
  useEffect(() => { setTab(0) }, [contact?.id])

  const { hasAction } = usePermission()
  const canCallLog = hasAction('/contacts', 'call_log')

  const { data: activityLogs = [], isLoading: loadingActivity } = useQuery<ContactActivity[]>({
    queryKey: ['contact-activities', contact?.id],
    queryFn: () => contactsApi.getActivity(contact!.id),
    enabled: !!contact && tab === 1,
  })

  if (!contact) return null

  const color = avatarColor(contact.name)

  return (
    <Paper elevation={0} sx={{
      width: 300, flexShrink: 0,
      border: `1px solid ${themeColors.border}`,
      borderRadius: '14px', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
      maxHeight: 'calc(100vh - 180px)',
    }}>
      {/* ── Header ── */}
      <Box sx={{ background: themeColors.primary, '&:hover': { background: themeColors.primaryDark }, px: 2.5, pt: 2.5, pb: 4, textAlign: 'center', position: 'relative', flexShrink: 0 }}>
        <IconButton size="small" onClick={onClose}
          sx={{ position: 'absolute', top: 8, right: 8, color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.1)' } }}>
          <CloseIcon fontSize="small" />
        </IconButton>
        <Box sx={{
          width: 58, height: 58, borderRadius: '50%', bgcolor: color,
          border: '3px solid rgba(255,255,255,0.5)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: '20px', fontWeight: 800, color: '#fff', mx: 'auto', mb: 1.2,
        }}>
          {getInitials(contact.name)}
        </Box>
        <Typography fontWeight={800} fontSize="15px" color="#fff">{contact.name}</Typography>
        {contact.role && (
          <Typography fontSize="11.5px" color="rgba(255,255,255,0.8)" mt={0.3}>{contact.role}</Typography>
        )}
      </Box>

      {/* ── Tabs ── */}
      <Box sx={{ borderBottom: `1px solid ${themeColors.border}`, flexShrink: 0 }}>
        <Tabs
          value={tab}
          onChange={(_e, v) => setTab(v)}
          sx={{
            minHeight: 38,
            '& .MuiTab-root': { minHeight: 38, textTransform: 'none', fontWeight: 600, fontSize: '12px', px: 2 },
            '& .MuiTabs-indicator': { bgcolor: themeColors.primary, height: 2.5, borderRadius: '2px 2px 0 0' },
            '& .Mui-selected': { color: `${themeColors.primary} !important` },
          }}
        >
          <Tab label="Details" />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <ActivityIcon sx={{ fontSize: 13 }} />
              Activity
              {activityLogs.length > 0 && (
                <Chip label={activityLogs.length} size="small"
                  sx={{ height: 16, fontSize: '9px', fontWeight: 700, bgcolor: themeColors.primaryLight, color: '#ffffff', ml: 0.3 }} />
              )}
            </Box>
          } />
          <Tab label={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <PhoneIcon sx={{ fontSize: 13 }} />
              Call
            </Box>
          } />
        </Tabs>
      </Box>

      {/* ── Tab Content ── */}
      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {/* Tab 0: Details */}
        {tab === 0 && (
          <Box>
            {/* Floating details card (overlaps header) */}
            <Box sx={{ mx: 2, mt: -2 }}>
              <Paper elevation={2} sx={{ borderRadius: '10px', p: 2, bgcolor: '#fff' }}>
                {[
                  { icon: <PhoneIcon sx={{ fontSize: 15 }} />, label: 'Phone', value: maskPhoneNumber(contact.phone) },
                  { icon: <EmailIcon sx={{ fontSize: 15 }} />, label: 'Email', value: contact.email },
                  { icon: <BusinessIcon sx={{ fontSize: 15 }} />, label: 'Account', value: contact.customer.companyName ?? contact.customer.name },
                  { icon: <RoleIcon sx={{ fontSize: 15 }} />, label: 'Role', value: contact.role },
                ].filter(r => r.value).map((row, i, arr) => (
                  <Box key={i} sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, py: 0.9,
                    borderBottom: i < arr.length - 1 ? `1px solid ${themeColors.borderLight}` : 'none'
                  }}>
                    <Box sx={{ color: themeColors.primary, display: 'flex', flexShrink: 0 }}>{row.icon}</Box>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography fontSize="10px" color="text.secondary" lineHeight={1.2}>{row.label}</Typography>
                      <Typography fontSize="12.5px" fontWeight={500} noWrap>{row.value}</Typography>
                    </Box>
                  </Box>
                ))}
              </Paper>
            </Box>

            {/* Metadata: Added by + created date */}
            <Box sx={{ px: 2, mt: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <DateIcon sx={{ fontSize: 12, color: themeColors.text.muted }} />
                <Typography fontSize="11.5px" color="text.secondary">
                  Added on {formatDate(contact.createdAt)}
                </Typography>
              </Box>
              {contact.createdBy && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {contact.createdBy.avatarUrl ? (
                    <Avatar src={contact.createdBy.avatarUrl} sx={{ width: 16, height: 16 }} />
                  ) : (
                    <Box sx={{
                      width: 16, height: 16, borderRadius: '50%', bgcolor: themeColors.primary,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '8px', fontWeight: 700, color: '#fff', flexShrink: 0,
                    }}>
                      {getInitials(contact.createdBy.name)}
                    </Box>
                  )}
                  <Typography fontSize="11.5px" color="text.secondary">
                    Added by <strong>{contact.createdBy.name}</strong>
                  </Typography>
                </Box>
              )}
            </Box>

            {/* Customer card */}
            <Box sx={{ px: 2, mt: 2 }}>
              <Typography fontSize="11px" color="text.secondary" mb={0.5} fontWeight={600} textTransform="uppercase" letterSpacing="0.5px">
                Customer
              </Typography>
              <Tooltip title={canViewCustomer ? 'View customer profile' : 'No permission to view customers'} placement="top">
                <Box
                  onClick={() => canViewCustomer && navigate(`/customers/${contact.customerId}`)}
                  sx={{
                    display: 'flex', alignItems: 'center', gap: 1.5, p: 1.5,
                    border: `1px solid ${themeColors.border}`, borderRadius: '8px',
                    bgcolor: '#fafafa',
                    cursor: canViewCustomer ? 'pointer' : 'default',
                    transition: 'border-color 0.15s, background 0.15s, box-shadow 0.15s',
                    ...(canViewCustomer && {
                      '&:hover': {
                        bgcolor: themeColors.primaryLight + '15',
                        borderColor: themeColors.primary,
                        boxShadow: `0 0 0 1px ${themeColors.primary}`,
                      },
                    }),
                  }}
                >
                  <Avatar sx={{ width: 30, height: 30, fontSize: '11px', fontWeight: 700, bgcolor: themeColors.primary }}>
                    {getInitials(contact.customer.name)}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      fontSize="12.5px" fontWeight={600} noWrap
                      sx={{ color: canViewCustomer ? themeColors.primary : 'text.primary' }}
                    >
                      {contact.customer.name}
                    </Typography>
                    {contact.customer.companyName && (
                      <Typography fontSize="11px" color="text.secondary" noWrap>{contact.customer.companyName}</Typography>
                    )}
                  </Box>
                  {canViewCustomer && (
                    <ViewIcon sx={{ fontSize: 14, color: themeColors.primary, opacity: 0.7, flexShrink: 0 }} />
                  )}
                </Box>
              </Tooltip>
            </Box>

            {/* Bottom padding */}
            <Box sx={{ pb: 3 }} />
          </Box>
        )}

        {/* Tab 1: Activity */}
        {tab === 1 && (
          <Box sx={{ p: 2 }}>
            <UniversalActivityTimeline logs={activityLogs} isLoading={loadingActivity} entityType="contact" />
          </Box>
        )}

        {/* Tab 2: Call */}
        {tab === 2 && (
          <Box>
            {canCallLog && (
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, p: 2 }}>
                <Button size="small" variant="contained" startIcon={<PhoneIcon sx={{ fontSize: '13px !important' }} />}
                  disabled={callStore.isStarting}
                  onClick={() => callStore.showPlaceCall({
                    entityType: 'CONTACT',
                    entityId: contact.id,
                    name: contact.name,
                    phone: contact.phone,
                    companyName: contact.customer.companyName ?? contact.customer.name
                  })}
                  sx={{
                    borderRadius: '8px', textTransform: 'none', fontSize: '12px', fontWeight: 600, background: '#7b1fa2',
                    '&:hover': { background: '#6a1b9a' }
                  }}>
                  {callStore.isStarting ? <CircularProgress size={13} sx={{ color: '#fff', mr: 0.5 }} /> : null}
                  Log Call
                </Button>
              </Box>
            )}
            <Box sx={{ px: 2, pb: 2 }}>
              <CallHistorySectionGeneric
                entityId={contact.id}
                entityType="contact"
                emptyHint="Use the Log Call button to start a call"
              />
            </Box>
          </Box>
        )}
      </Box>

      {/* ── Actions (always visible at bottom) ── */}
      {(canEdit || canDelete) && (
        <Box sx={{ px: 2, py: 1.5, borderTop: `1px solid ${themeColors.border}`, display: 'flex', gap: 1, flexShrink: 0, bgcolor: '#fff' }}>
          {canEdit && (
            <Button fullWidth size="small" variant="outlined" startIcon={<EditIcon sx={{ fontSize: 14 }} />}
              onClick={() => onEdit(contact)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '12.5px' }}>
              Edit
            </Button>
          )}
          {canDelete && (
            <Button fullWidth size="small" variant="outlined" color="error"
              startIcon={<DeleteIcon sx={{ fontSize: 14 }} />}
              onClick={() => onDelete(contact)}
              sx={{ textTransform: 'none', borderRadius: '8px', fontWeight: 600, fontSize: '12.5px' }}>
              Delete
            </Button>
          )}
        </Box>
      )}

      {/* Global calling UI overlay (rendered in AppLayout) */}
    </Paper>
  )
})

// ── Main Page ─────────────────────────────────────────────────────────────────
const ContactsPage = observer(function ContactsPage() {
  const qc = useQueryClient()
  const navigate = useNavigate();

  const { hasAction } = usePermission()
  // SuperAdmin detection: OrgId/LobId-based (matches the navbar org selector), not
  // usePermission's roleName-string match — that doesn't reliably match every
  // SuperAdmin's role label, which previously hid the Organisation/LOB filters here.
  const { isSuperAdmin } = useOrgSelector()
  const canCreate       = hasAction('/contacts', 'create')
  const canEdit         = hasAction('/contacts', 'update')
  const canDelete       = hasAction('/contacts', 'delete')
  const canViewCustomer = hasAction('/customers', 'read')

  // Org Admin tier (mirrors backend ITenantContext convention): has an orgId but no
  // specific lobId — sees/filters across every LOB in their own org, unlike a Normal User.
  const userOrgId  = authStore.user?.orgId ?? ''
  const userLobId  = authStore.user?.lobId ?? ''
  const isOrgAdmin = !isSuperAdmin && !!userOrgId && !userLobId

  // Global org filter from navbar; local page dropdown takes precedence when set
  const navOrgFilter = useOrgFilter()

  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<UnifiedFilters>(() => {
    const saved = sessionStorage.getItem('crm-contacts-filters')
    if (saved) { try { return JSON.parse(saved) } catch {} }
    return EMPTY_UNIFIED_FILTERS
  })
  const [page, setPage] = useState(1)

  useEffect(() => {
    sessionStorage.setItem('crm-contacts-filters', JSON.stringify(filters))
  }, [filters])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [detailContact, setDetailContact] = useState<Contact | null>(null)
  const [editingContact, setEditingContact] = useState<Contact | null>(null)
  const [deletingContact, setDeletingContact] = useState<Contact | null>(null)
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false)
  const [showNewForm, setShowNewForm] = useState(false)

  const [pageSizeTable, setPageSizeTable] = useState(() => {
    const saved = localStorage.getItem('crm_contacts_page_size')
    return saved ? Number(saved) : 10
  })

  const { data: orgsData = [] } = useQuery({
    queryKey: ['admin-orgs'],
    queryFn: orgsApi.getAll,
    enabled: isSuperAdmin,
    staleTime: 60_000,
  })

  // Prefer the org picked in this page's own filter dialog; fall back to whatever
  // org is selected in the navbar so the LOB list works right after picking an org
  // up top, without needing to also set the local Organisation filter.
  const orgIdForFetching = filters.checkboxes.orgId?.[0] || navOrgFilter.orgId
  // SuperAdmin: LOBs depend on whichever org is in scope (filter or navbar selection).
  // Org Admin: LOBs are always scoped to their own (fixed) org — no org selector needed.
  const lobOrgIdForFetching = isSuperAdmin ? orgIdForFetching : userOrgId
  const { data: lobsForFilter = [] } = useQuery({
    queryKey: ['admin-lobs', lobOrgIdForFetching],
    queryFn: () => lobsApi.getAll(lobOrgIdForFetching!),
    enabled: (isSuperAdmin || isOrgAdmin) && !!lobOrgIdForFetching,
    staleTime: 30_000,
  })

  const filterDefs: FilterDef[] = useMemo(() => {
    const defs: FilterDef[] = []
    if (isSuperAdmin) {
      defs.push(
        {
          type: 'checkbox',
          key: 'orgId',
          label: 'Organisation',
          options: orgsData.map(o => ({ label: o.name, value: o.id })),
        },
        {
          type: 'checkbox',
          key: 'lobId',
          label: 'LOB',
          options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
        },
      )
    } else if (isOrgAdmin) {
      // Org Admin's org is fixed (no Organisation picker needed) — just let them
      // narrow the list down to one or more LOBs within their own org.
      defs.push({
        type: 'checkbox',
        key: 'lobId',
        label: 'LOB',
        options: lobsForFilter.map(l => ({ label: l.name, value: l.id })),
      })
    }
    if (isSuperAdmin || isOrgAdmin) defs.push({ type: 'owner', label: 'Owner' })
    defs.push({ type: 'date', label: 'Date Created' })
    return defs
  }, [isSuperAdmin, isOrgAdmin, orgsData, lobsForFilter])

  // Local page filter takes precedence; navbar org filter is the fallback for SuperAdmin
  const effectiveOrgIds = isSuperAdmin && filters.checkboxes.orgId?.length
    ? filters.checkboxes.orgId
    : navOrgFilter.orgId ? [navOrgFilter.orgId] : undefined

  const effectiveLobIds = (isSuperAdmin || isOrgAdmin) && filters.checkboxes.lobId?.length
    ? filters.checkboxes.lobId
    : undefined

  const { data, isLoading } = useQuery({
    queryKey: ['contacts-list', { search, orgIds: effectiveOrgIds, lobIds: effectiveLobIds, filters, page, pageSize: pageSizeTable }],
    queryFn: () => contactsApi.getAll({
      search: search || undefined,
      orgIds: effectiveOrgIds,
      lobIds: effectiveLobIds,
      ownerId: filters.ownerIds[0] || undefined,
      fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
      toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
      page,
      pageSize: pageSizeTable,
    }),
    placeholderData: prev => prev,
  })

  const contacts = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = Math.max(1, Math.ceil(total / pageSizeTable))

  const updateMutation = useMutation({
    mutationFn: ({ id, p }: { id: string; p: UpdateContactPayload }) => contactsApi.update(id, p),
    onSuccess: (updated) => {
      qc.invalidateQueries({ queryKey: ['contacts-list'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      qc.invalidateQueries({ queryKey: ['contact-activities', updated.id] })
      setEditingContact(null)
      if (detailContact?.id === updated.id) setDetailContact(updated)
      toast.success('Contact updated')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to update'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => contactsApi.delete(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['contacts-list'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      if (detailContact?.id === deletingContact?.id) setDetailContact(null)
      setDeletingContact(null)
      toast.success('Contact deleted')
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete'),
  })

  const bulkDeleteMutation = useMutation({
    mutationFn: async () => {
      const ids = Array.from(selected)
      await Promise.all(ids.map(id => contactsApi.delete(id)))
      return ids.length
    },
    onSuccess: (count) => {
      qc.invalidateQueries({ queryKey: ['contacts-list'] })
      qc.invalidateQueries({ queryKey: ['contacts'] })
      setSelected(new Set())
      setBulkDeleteOpen(false)
      toast.success(`${count} contact(s) deleted`)
    },
    onError: (e: any) => toast.error(e?.response?.data?.message ?? 'Failed to delete contacts'),
  })

  const bulkActions: BulkAction[] = useMemo(() => {
    const actions: BulkAction[] = []
    if (canDelete) {
      actions.push({
        label: 'Delete',
        color: 'error',
        onClick: () => setBulkDeleteOpen(true),
      })
    }
    return actions
  }, [canDelete])

  // ── Column definitions ────────────────────────────────────────────────────
  const contactColumns: Column[] = [
    {
      id: 'name',
      label: 'Contact Name',
      render: (_, contact: Contact) => contact.name,
    },
    { id: 'phone', label: 'Phone Number', render: (_, contact: Contact) => maskPhoneNumber(contact.phone) },
    { id: 'email', label: 'Email', render: (_, contact: Contact) => contact.email ?? '-' },
    { id: 'company', label: 'Company Name', render: (_, contact: Contact) => contact.customer.companyName ?? contact.customer.name },
    { id: 'role', label: 'Designation', render: (_, contact: Contact) => contact.role ?? '-' },
    { id: 'owner', label: 'Contact Owner', render: (_, contact: Contact) => contact.createdBy?.name ?? '-' },
    {
      id: 'actions',
      label: 'Actions',
      align: 'right',
      width: 96,
      sortable: false,
      render: (_, contact: Contact) => (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.25 }} onClick={e => e.stopPropagation()}>
          {canEdit && (
            <Tooltip title="Edit contact">
              <IconButton size="small" onClick={() => setEditingContact(contact)}
                sx={{ color: '#7A7580', '&:hover': { color: '#2F1E57', bgcolor: '#F2EEF9' } }}>
                <EditActionIcon />
              </IconButton>
            </Tooltip>
          )}
          {canDelete && (
            <Tooltip title="Delete contact">
              <IconButton size="small" onClick={() => setDeletingContact(contact)}
                sx={{ color: '#D92D20', '&:hover': { bgcolor: '#FFE5E5' } }}>
                <DeleteActionIcon />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      ),
    },
  ]

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <PageHeader
        title="Contacts"
        breadcrumb="CRM"
        actions={
          canCreate ? (
            <PageActionButtons
              onNew={() => setShowNewForm(true)}
              onExport={async () => {
                const apiFilters = {
                  orgIds: effectiveOrgIds,
                  lobIds: effectiveLobIds,
                  ownerId: filters.ownerIds[0] || undefined,
                  fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
                  toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
                }
                await contactsApi.export({ ...apiFilters, search })
              }}
              newLabel="New Contact"
            />
          ) : (
            <PageActionButtons
              onExport={async () => {
                const apiFilters = {
                  orgIds: effectiveOrgIds,
                  lobIds: effectiveLobIds,
                  ownerId: filters.ownerIds[0] || undefined,
                  fromDate: filters.dateFrom ? `${filters.dateFrom}T00:00:00Z` : undefined,
                  toDate: filters.dateTo ? `${filters.dateTo}T23:59:59Z` : undefined,
                }
                await contactsApi.export({ ...apiFilters, search })
              }}
            />
          )
        }
      />


      {/* ── Pinned toolbar ── */}
      <Box sx={{ px: 3, pt: 3, pb: 0, flexShrink: 0 }}>
        <ListToolbar
          search={search}
          onSearch={(v) => { setSearch(v); setPage(1); setSelected(new Set()) }}
          searchPlaceholder="Search name, phone, email, company..."
          filterDefs={filterDefs}
          filters={filters}
          onFilterChange={(f) => { setFilters(f); setPage(1) }}
          selectedCount={selected.size}
          bulkActions={bulkActions}
          onClearSelection={() => setSelected(new Set())}
        />
      </Box>

      {/* ── Scrollable content + optional side panel ── */}
      <Box sx={{ flex: 1, px: 3, pb: 3, pt: 2, display: 'flex', gap: 2.5, alignItems: 'stretch', minHeight: 0 }}>
        {/* ── Main table panel ── */}
        <Box sx={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column' }}>

          {/* Table */}
          <DataTable
            columns={contactColumns}
            rows={contacts}
            isLoading={isLoading}
            selectable
            showRowNumbers={false}
            selectedIds={selected}
            onSelectionChange={setSelected}
            onRowClick={(contact: Contact) => navigate(`/contacts/${contact.id}`)}
            emptyIcon={<PersonIcon />}
            emptyMessage={search || countActiveFilters(filters) > 0 ? 'No contacts match your filters' : 'No contacts found'}
            emptyHint={search || countActiveFilters(filters) > 0 ? 'Try adjusting your filters' : 'Contacts will appear here once customers have contacts'}
            getRowSx={(contact: Contact) => detailContact?.id === contact.id ? { backgroundColor: '#F7F3FF', '&:hover': { backgroundColor: '#F4EEFF' } } : {}}
            pagination={{
              page,
              pageSize: pageSizeTable,
              total,
              totalPages,
              itemLabel: 'results',
              onPageChange: setPage,
              onPageSizeChange: s => { setPageSizeTable(s); localStorage.setItem('crm_contacts_page_size', String(s)); setPage(1) },
              pageSizeOptions: [10, 20, 50, 100],
            }}
          />
        </Box>

        {/* ── Detail side panel ── */}
        {detailContact && (
          <ContactDetailPanel
            contact={detailContact}
            onClose={() => setDetailContact(null)}
            canEdit={canEdit}
            canDelete={canDelete}
            canViewCustomer={canViewCustomer}
            onEdit={(c) => { setEditingContact(c) }}
            onDelete={(c) => { setDeletingContact(c) }}
          />
        )}
      </Box>

      {/* ── Dialogs ── */}
      {editingContact && (
        <EditContactDialog
          open={!!editingContact}
          contact={editingContact}
          onClose={() => setEditingContact(null)}
          onSave={async p => { await updateMutation.mutateAsync({ id: editingContact.id, p }) }}
          isSaving={updateMutation.isPending}
        />
      )}
      <DeleteDialog
        open={!!deletingContact}
        title="Delete Contact"
        name={deletingContact?.name}
        onClose={() => setDeletingContact(null)}
        onConfirm={() => deletingContact && deleteMutation.mutate(deletingContact.id)}
        isDeleting={deleteMutation.isPending}
      />

      <DeleteDialog
        open={bulkDeleteOpen}
        title={`Delete ${selected.size} Contact(s)`}
        description={`Are you sure you want to delete ${selected.size} selected contact(s)? This action cannot be undone.`}
        isDeleting={bulkDeleteMutation.isPending}
        onClose={() => setBulkDeleteOpen(false)}
        onConfirm={() => bulkDeleteMutation.mutate()}
      />

      {/* New Contact — drawer */}
      <ContactDrawer
        open={showNewForm}
        onClose={() => setShowNewForm(false)}
        isSuperAdmin={isSuperAdmin}
        onCreated={() => {
          qc.invalidateQueries({ queryKey: ['contacts-list'] })
          qc.invalidateQueries({ queryKey: ['contacts'] })
        }}
      />
    </Box>
  )
})

export default ContactsPage

